import { createDeferred, type Deferred } from 'helpers/deferredPromise';
import { create } from 'zustand';

export type DrawerInstance<T = void> = {
    id: string;
    props?: {
        disableOverlay?: boolean;
        [key: string]: unknown;
    };
    animate?: boolean;
    animation?: Deferred<T>;
};

type State = {
    drawerInstances: DrawerInstance[];
};

const initialState: State = {
    drawerInstances: []
};

export const useDrawerStore = create<State>()(() => ({
    ...initialState
}));

function appendAnimationToDrawerInstance(drawerInstance: DrawerInstance) {
    if (!drawerInstance.animation) {
        // Create a promise that will be resolved by AnimatedDrawerViewComponent's onRest callback.
        drawerInstance.animation = createDeferred();
    }
    return drawerInstance;
}

const openDrawer = async (drawerInstance: DrawerInstance) => {
    const animatedDrawerInstance = appendAnimationToDrawerInstance(drawerInstance);

    useDrawerStore.setState((state) => ({
        drawerInstances: [...state.drawerInstances, animatedDrawerInstance]
    }));

    // Await animation completion (will resolve when onRest fires)
    await animatedDrawerInstance.animation?.promise;
};

export const closeDrawer = (id: DrawerInstance['id']) => {
    useDrawerStore.setState((state) => {
        return {
            drawerInstances: state.drawerInstances.filter((drawerInstance) => drawerInstance.id !== id)
        };
    });
};

export const closeAllDrawers = () => useDrawerStore.setState(initialState);

const toggleDrawer = (drawerInstance: DrawerInstance) => {
    const { id } = drawerInstance;
    const isOpen = !!useDrawerStore.getState().drawerInstances.find((instance) => instance.id === id);

    if (isOpen) {
        closeDrawer(id);
    } else {
        openDrawer(drawerInstance);
    }
};

export const openDrawers = (drawerInstances: DrawerInstance[]) => {
    const animatedDrawerInstances = drawerInstances.map(appendAnimationToDrawerInstance);

    useDrawerStore.setState((state) => ({ drawerInstances: [...state.drawerInstances, ...animatedDrawerInstances] }));

    return Promise.all(animatedDrawerInstances.map((drawerInstance) => drawerInstance.animation?.promise));
};

const useDrawer = (id: DrawerInstance['id'], props?: DrawerInstance['props']) => {
    const isOpen = useIsDrawerOpen(id);

    const open = async () => openDrawer({ id, props });

    const close = () => closeDrawer(id);

    const toggle = () => toggleDrawer({ id, props });

    return { open, close, toggle, isOpen };
};

export const useDrawerInstance = (id: DrawerInstance['id']) => {
    return useDrawerStore((state) => state.drawerInstances.find((drawerInstance) => drawerInstance.id === id));
};

// Imperative (non-hook) variant for usage outside React components.
export const isDrawerOpen = (id: DrawerInstance['id']) => {
    return useDrawerStore.getState().drawerInstances.some((d) => d.id === id);
};

// Hook subscribes to drawer state and reuses the same logic inline (kept explicit for readability).
export const useIsDrawerOpen = (id: DrawerInstance['id']) => {
    return useDrawerStore((state) => state.drawerInstances.some((d) => d.id === id));
};

export const useDrawerProps = <T extends Record<string, unknown>>(id: DrawerInstance['id']) => {
    const drawerInstance = useDrawerInstance(id);
    return (drawerInstance?.props || {}) as T;
};

export const useHasActiveDrawerInstances = () => {
    const hasActiveDrawerInstances = useDrawerStore((state) => state.drawerInstances.length > 0);
    return hasActiveDrawerInstances;
};

export default useDrawer;
